import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { StatusCode } from "api/protocols";
import { GetCustomer, PostCustomer, SendBirthdayRequestEmails } from "api/rpc/2024-04/facilityAdmin/customer/customer";
import { GetBookingCategories } from "api/rpc/2024-04/facilityAdmin/teesheet/bookingCategory";
import { IBookingCategory } from "redux/reducers/models/teetime";

import { showError, showSuccess } from "redux/actions/ui";
import { filterHistoryAddItem, filterHistoryCreate } from "redux/actions/filters/filterHistory";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import { customerErrorMessage } from "helpers/Helpers";
import useModal from "hooks/modals/useModal";

import Page from "components/page/Page";
import NewCustomerDrawer from "components/newCustomer/NewCustomerDrawer";
import { ICustomerInfoState } from "components/newCustomer/NewCustomer";
import Search from "components/search/Search";
import CustomerTable from "elements/customer/CustomerTable";
import DropFilter from "components/dropdown/DropFilter";
import { ButtonNew as Button } from "components/buttonNew";
import Popup from "components/popup/Popup";
import SelectNew from "components/select/SelectNew";

import "elements/customer/customer.scss";
import { GetCustomerType } from "api/rpc/2024-04/facilityAdmin/customer/type";
import ExportCustomers from "components/exportCustomers/ExportCustomers";
import ImportCustomers from "components/importCustomers/ImportCustomers";
import MultipleActionDropdown from "components/multipleActionDropdown/MultipleActionDropdown";
import { IMultipleActionDropdownPageAction } from "components/page/PageActions";

interface IFilterState {
  search: string;
  limit: number;
  offset: number;
  customer_type: number[];
  cart_customer_type: number[];
}

const OFFSET_HISTORY_KEY = "facility_customers_offset";
const TABLE_LIMIT = 50;

export default function Customers() {
  const { t } = useTranslation();
  const history = useHistory();

  const dispatch = useAppDispatch();
  const filterHistoryStore = useAppSelector(store => store.filterHistoryStore);
  const windowSize = useWindowSize();

  const [showImportModal, setShowImportModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [birthdayRequestVisible, setBirthdayRequestVisible] = useState(false);
  const [customerTypes, setCustomerTypes] = useState(null);
  const [customers, setCustomers] = useState(undefined);
  const [filter, setFilter] = useState<IFilterState>({
    search: "",
    offset: filterHistoryStore[OFFSET_HISTORY_KEY]?.currentFilter ?? 0,
    limit: TABLE_LIMIT,
    customer_type: null,
    cart_customer_type: null,
  });

  const { state, updateModal, closeModal } = useModal();

  useEffect(() => {
    void loadCustomerTypes();
  }, []);

  // Search Products
  useEffect(() => {
    const source = axios.CancelToken.source();

    void loadCustomers(source.token);

    return () => {
      source.cancel();
    };
  }, [filter.search, filter.offset, filter.customer_type, filter.cart_customer_type]);

  useEffect(() => {
    if (filterHistoryStore[OFFSET_HISTORY_KEY] === undefined) {
      dispatch(filterHistoryCreate(OFFSET_HISTORY_KEY));
    }
  }, []);

  function navigateToCustomer(customerId: number) {
    if (filterHistoryStore[OFFSET_HISTORY_KEY] === undefined) {
      dispatch(filterHistoryCreate(OFFSET_HISTORY_KEY));
    } else {
      dispatch(filterHistoryAddItem(OFFSET_HISTORY_KEY, filter.offset));
    }

    history.replace({
      pathname: history.location.pathname,
      search: history.location.search,
      state: {
        params: { customerId },
      },
    });

    history.push("/admin/customers/" + String(customerId) + "/profile");
  }

  async function loadCustomers(token?: CancelToken) {
    if (customers !== undefined) {
      setCustomers(undefined);
    }

    const customerRes = await GetCustomer(
      {
        "default-address-line": true,
        limit: TABLE_LIMIT,
        offset: filter.offset,
        search: filter.search,
        customer_type: filter.customer_type,
        cart_customer_type: filter.cart_customer_type,
      },
      token ? false : true,
      token,
    );

    if (token && token.reason) {
      return;
    }
    if (customerRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading customers")));
    }

    setCustomers(customerRes.status !== StatusCode.OK ? [] : customerRes.data);
  }

  async function loadCustomerTypes() {
    const res = await GetCustomerType({ limit: 1000 }, false);

    if (res.status !== StatusCode.OK) {
      return;
    }

    setCustomerTypes(res.data);
  }

  async function createCustomer(customerInfo: ICustomerInfoState): Promise<number | null> {
    const error =
      customerInfo.firstName === "" ||
      customerInfo.lastName === "" ||
      (customerInfo.emailAddress === "" && customerInfo.phoneNumber === "");

    if (error) {
      return null;
    }

    const customerRes = await PostCustomer(
      {
        first_name: customerInfo.firstName,
        last_name: customerInfo.lastName,
        phone: customerInfo.phoneNumber || null,
        email: customerInfo.emailAddress || null,
        send_welcome_email: customerInfo.sendWelcomeEmail,
        marketing_email: customerInfo.sendMarketingEmails ?? false,
      },
      true,
    );

    if (customerRes.status !== StatusCode.OK) {
      dispatch(showError(customerErrorMessage(t, customerRes?.message)));
      return null;
    }

    void loadCustomers();
    const customerId = customerRes.data?.id;
    return typeof customerId === "number" ? customerId : null;
  }

  function handleTableNavigation(direction: "prev" | "next") {
    switch (direction) {
      case "prev": {
        setFilter(prev => ({ ...prev, offset: filter.offset - TABLE_LIMIT }));
        break;
      }
      case "next": {
        setFilter(prev => ({ ...prev, offset: filter.offset + TABLE_LIMIT }));
        break;
      }
      default:
        return;
    }
  }

  function getImportTemplate() {
    const template = document.createElement("a");
    template.href = "data:text/csv;charset=utf-8," + encodeURI("first_name,last_name,email,phone,username,customer_type,customer_cart_type");
    template.target = "_blank";
    template.download = "customer_import_template.csv";
    template.click();
  }

  const addCustomerAction = {
    content: (
      <div>
        <FontAwesomeIcon className="mr-2" icon={["far", "plus"]} />
        {t("Add Customer")}
      </div>
    ),
    action: () => updateModal({ isOpen: true }),
  };

  const secondaryActions: IMultipleActionDropdownPageAction = {
    label: t('Quick Actions'),
    dropdownProps: {
      alignment: windowSize.width > MOBILE_WIDTH ? "right" : "left",
      options: [
        {
          type: 'handler',
          label: t('Export Customer List'),
          handler: () => setShowExportModal(true),
          icon: 'file-export',
        },
        {
          type: "handler",
          label: t("Import Template"),
          handler: () => getImportTemplate(),
          icon: "file-csv",
        },
        {
          type: 'handler',
          label: t('Import Customer List'),
          handler: () => setShowImportModal(true),
          icon: "cloud-arrow-up",
        },
        {
          type: 'handler',
          label: t('Request Birthdays'),
          handler: () => setBirthdayRequestVisible(true),
          icon: "send",
        },
      ],
    },
  };

  return (
    <Page
      title={t("Customers")}
      primaryAction={windowSize.width > MOBILE_WIDTH ? addCustomerAction : undefined}
      multipleActionDropdownAction={windowSize.width > MOBILE_WIDTH ? secondaryActions : undefined}
    >
      {customerTypes && (
        <div className="flex flex-row flex-wrap gap-4 mb-2">
          <DropFilter
            title={t("Customer Type")}
            filterData={customerTypes.filter((type: any) => type.application === "green_fee")}
            filterLabelPropFromData="title"
            filterIdPropFromData="id"
            filterType="Checkbox"
            applyFilters={filterReturn =>
              setFilter({
                ...filter,
                customer_type: filterReturn.map(filter => filter.id as number),
              })
            }
            save
            historyKey="customer_type"
            leftAligned
            subtitle
          />

          <DropFilter
            title={t("Cart Type")}
            filterData={customerTypes.filter((type: any) => type.application === "power_cart")}
            filterLabelPropFromData="title"
            filterIdPropFromData="id"
            filterType="Checkbox"
            applyFilters={filterReturn =>
              setFilter({
                ...filter,
                cart_customer_type: filterReturn.map(filter => filter.id as number),
              })
            }
            save
            historyKey="cart_customer_type"
            leftAligned
          />
        </div>
      )}
      <div className="flex-grow mb-3.5">
        <Search
          placeholder={t("Search customers by name, membership, address, or phone...")}
          historyKey={"search_customers_table"}
          searchCallback={searchValue =>
            setFilter(prev => ({
              ...prev,
              search: searchValue,
              offset: searchValue === prev.search ? prev.offset : 0,
            }))
          }
        />
      </div>
      <div className="customers-add-customer-actions-mobile">
        <MultipleActionDropdown {...secondaryActions.dropdownProps} disabled={secondaryActions.disabled}>
          <Button size="medium" type="secondary" disabled={!!secondaryActions?.disabled}>
            {secondaryActions.label}
          </Button>
        </MultipleActionDropdown>
        <Button type="primary" size="medium" onClick={addCustomerAction.action}>
          {addCustomerAction.content}
        </Button>
      </div>

      <CustomerTable
        customers={customers}
        onClick={customer => navigateToCustomer(customer.id)}
        tableLimit={TABLE_LIMIT}
        tableOffset={filter.offset}
        handlePagination={direction => handleTableNavigation(direction)}
      />

      <NewCustomerDrawer
        open={state.isOpen}
        onClose={closeModal}
        onCreateCustomer={createCustomer}
        onNavigateToProfile={customerId => history.push("/admin/customers/" + String(customerId) + "/profile")}
        searchValue={filter.search}
      />

      <ExportCustomers
        type="facility"
        open={showExportModal}
        filter={filter}
        onClose={() => setShowExportModal(false)}
      />
      <ImportCustomers
        type="facility"
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
      />

      <BirthdayRequestPopup open={birthdayRequestVisible} onClose={() => setBirthdayRequestVisible(false)} />
    </Page>
  );
}

function BirthdayRequestPopup(props: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [bookingCategories, setBookingCategories] = useState<IBookingCategory[]>([]);
  const [bookingCategoryIds, setBookingCategoryIds] = useState<number[]>([]);

  useEffect(() => {
    if (!props.open) {
      return;
    }

    setBookingCategoryIds([]);

    if (bookingCategories.length > 0) {
      return;
    }

    void loadBookingCategories();
  }, [props.open]);

  async function loadBookingCategories() {
    const response = await GetBookingCategories({}, false);
    if (response.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading booking categories")));
      return;
    }

    setBookingCategories(response.data ?? []);
  }

  function toggleBookingCategory(categoryId: number) {
    setBookingCategoryIds(current =>
      current.includes(categoryId) ? current.filter(id => id !== categoryId) : [...current, categoryId],
    );
  }

  async function sendBirthdayRequests() {
    const response = await SendBirthdayRequestEmails(
      bookingCategoryIds.length > 0 ? { booking_category_ids: bookingCategoryIds } : {},
      true,
    );

    if (response.status !== StatusCode.OK) {
      dispatch(showError(t("Error sending birthday request emails")));
      return;
    }

    dispatch(showSuccess(t("Birthday request emails are being sent")));
    props.onClose();
  }

  const placeholder =
    bookingCategoryIds.length === 0
      ? t("All booking categories")
      : bookingCategories
          .filter(category => bookingCategoryIds.includes(category.id))
          .map(category => category.title)
          .join(", ");

  return (
    <Popup
      open={props.open}
      title={t("Request Birthdays")}
      type="info"
      aligned="left"
      description={t("An email will be sent to customers who have an email address and do not have a birthday on file. The email asks them to add their birthday and review their profile.")}
      onCancel={props.onClose}
      onOk={sendBirthdayRequests}
      okText={t("Send")}
    >
      <SelectNew
        id="birthday-request-booking-categories"
        className="mt-4"
        label={t("Booking Categories")}
        placeholder={placeholder}
        helpText={t("Leave blank to include customers in every booking category.")}
        multiValue={bookingCategoryIds}
        usePortal
        options={bookingCategories.map(category => ({
          id: category.id,
          label: category.title,
        }))}
        onChange={toggleBookingCategory}
      />
    </Popup>
  );
}


